import { useCallback, useEffect, useRef, useState } from 'react';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import { applyServerPreview, checkImportFile, importKind, parseImportText } from './wikiImport';
import { WIKI_IMPORT_ERRORS } from './wikiModalCopy';
import { wikiModalErrorText, wikiPlanRefusal } from './wikiModalErrors';
import { REFUND_POLICY_SAMPLE_MD, REFUND_POLICY_SAMPLE_NAME } from './refundPolicySample';

function readFileText(file) {
    return new Promise((resolve, reject) => {
        if (typeof FileReader === 'undefined') {
            reject(new Error('FileReader unavailable'));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('read failed'));
        reader.readAsText(file);
    });
}

function sampleFile() {
    const blob = typeof File === 'function'
        ? new File([REFUND_POLICY_SAMPLE_MD], REFUND_POLICY_SAMPLE_NAME, { type: 'text/markdown' })
        : new Blob([REFUND_POLICY_SAMPLE_MD], { type: 'text/markdown' });
    return blob;
}

/**
 * Import state for the New page modal (prototype `wkImport`):
 *   null                                  nothing chosen (chooser shown)
 *   { error }                             refused (chooser + red error box)
 *   { name, size, kind, pending:true }    .docx being converted on the server
 *   { name, size, kind, title, raw, blocks, warnings, token, uploading }
 *
 * .md/.markdown/.mdown/.txt are read and parsed in the browser immediately
 * (stats + preview at once, prototype `wkPickFile` → `wkSetImport`), then
 * uploaded to `POST /wiki/import/preview` for the single-use commit token;
 * .docx goes straight to the server and is previewed from its `body_md`.
 * A server refusal replaces the preview with its message; a PLAN refusal
 * (403 `wiki_feature_disabled` / `wiki_limit_reached`) goes to
 * `onPlanRefusal({code, key, message})` instead (the modal closes and toasts
 * it). Picking another file (or Replace) drops any in-flight result for the
 * previous one.
 */
export default function useWikiImportFile({ onPlanRefusal } = {}) {
    const client = useWikiV2Client();
    const [imp, setImp] = useState(null);
    const seqRef = useRef(0);
    const uploadRef = useRef(null);
    const onPlanRefusalRef = useRef(onPlanRefusal);

    useEffect(() => { onPlanRefusalRef.current = onPlanRefusal; }, [onPlanRefusal]);

    useEffect(() => () => { seqRef.current += 1; }, []);

    const upload = useCallback((file, seq, name) => {
        const form = new FormData();
        form.append('file', file, name);
        const promise = Promise.resolve()
            .then(() => client.postForm('/wiki/import/preview', form))
            .then((res) => {
                const token = res && res.preview_token;
                if (!token) throw new Error('The import preview returned no token.');
                if (seqRef.current === seq) {
                    setImp((prev) => (prev && prev.seq === seq ? applyServerPreview(prev, res.preview || {}, token) : prev));
                }
                return token;
            })
            .catch((error) => {
                if (seqRef.current === seq) {
                    const refusal = wikiPlanRefusal(error);
                    if (refusal && onPlanRefusalRef.current) {
                        setImp(null);
                        onPlanRefusalRef.current(refusal);
                    } else {
                        setImp({ error: wikiModalErrorText(error) });
                    }
                }
                throw error;
            });
        promise.catch(() => {});
        uploadRef.current = { seq, promise };
        return promise;
    }, [client]);

    const load = useCallback(async (file, { name: forcedName, text: knownText } = {}) => {
        seqRef.current += 1;
        const seq = seqRef.current;
        uploadRef.current = null;
        if (!file) return;
        const name = forcedName || file.name || '';
        const refusal = checkImportFile({ name, size: file.size });
        if (refusal) {
            setImp({ error: refusal });
            return;
        }
        const kind = importKind(name);
        if (kind === 'docx') {
            setImp({ seq, name, size: file.size, kind, pending: true, uploading: true });
            upload(file, seq, name);
            return;
        }
        let text = knownText;
        if (text == null) {
            try {
                text = await readFileText(file);
            } catch {
                if (seqRef.current === seq) setImp({ error: WIKI_IMPORT_ERRORS.unreadable });
                return;
            }
        }
        if (seqRef.current !== seq) return;
        setImp({ seq, kind, ...parseImportText(name, file.size, text), token: null, uploading: true, pending: false });
        upload(file, seq, name);
    }, [upload]);

    /** Prototype `wkUseSample`: the bundled refund-policy.md (705 bytes). */
    const loadSample = useCallback(() => {
        const file = sampleFile();
        return load(file, { name: REFUND_POLICY_SAMPLE_NAME, text: REFUND_POLICY_SAMPLE_MD });
    }, [load]);

    /** Prototype "Replace": back to the chooser. */
    const clear = useCallback(() => {
        seqRef.current += 1;
        uploadRef.current = null;
        setImp(null);
    }, []);

    /**
     * The commit token for the current file, waiting for an in-flight upload.
     * Rejects when there is no parsed file or the upload failed (the error is
     * already on screen).
     */
    const awaitToken = useCallback(() => {
        if (!imp || !Array.isArray(imp.blocks)) return Promise.reject(new Error('No file to import.'));
        if (imp.token) return Promise.resolve(imp.token);
        if (uploadRef.current && uploadRef.current.seq === imp.seq) return uploadRef.current.promise;
        return Promise.reject(new Error('The import is not ready.'));
    }, [imp]);

    return { imp, load, loadSample, clear, awaitToken };
}
